import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowUpTrayIcon, EyeIcon, PencilSquareIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { Button, Container, PageHeader, RowAction, Table } from "../ui";
import { canManageScholarships } from "../scholarships/access";
import { countryName } from "../scholarships/model";
import { useDebouncedSearch } from "../bursaries/shared";
import { ABROAD_KEY, durationLabel, useProgrammes } from "./model";
import { abroadAdminApi } from "../../api/abroadLoans";
import { problemOf } from "../../api/scholarships";
import ProgrammeImportDialog from "../scholarships/ProgrammeImportDialog";
import ProgrammeDialog from "./ProgrammeDialog";

const PAGE_SIZE = 20;

// One import row: a programme already in the catalogue answers 409 programme-exists and is left as it is
const saveImported = async (body) => {
  try {
    const { data } = await abroadAdminApi.createProgramme(body);
    return { id: data.id, existing: false };
  } catch (error) {
    if (problemOf(error)?.code === "programme-exists") return { id: null, existing: true };
    throw error;
  }
};

// The programmes abroad loan calls can offer, entered once and reused across calls
export default function ProgrammesPage() {
  const manage = canManageScholarships();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const { search, setSearch, debounced } = useDebouncedSearch(() => setPage(0));
  // `null` closed, `{}` a new programme, else the programme edited
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);

  const { data, isPending, isError } = useProgrammes({ q: debounced, page, size: PAGE_SIZE });

  const columns = [
    { key: "name", label: "Programme", render: (p) => <span className="font-medium text-slate-800">{p.name}</span> },
    { key: "levelName", label: "Level", accessor: (p) => p.levelName ?? "—" },
    { key: "durationMonths", label: "Duration", accessor: (p) => durationLabel(p.durationMonths), sortValue: (p) => p.durationMonths },
    { key: "institutionName", label: "Institution", accessor: (p) => p.institutionName ?? "Named by the student" },
    { key: "countryCode", label: "Country", accessor: (p) => (p.countryCode ? countryName(p.countryCode) : "Named by the student") },
    ...(manage ? [{ key: "actions", label: "", exportable: false, render: (p) => <RowAction Icon={PencilSquareIcon} label="Edit" onClick={() => setEditing(p)} /> }] : []),
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader
        title="Loan programmes"
        subtitle={data ? `${data.total} programme${data.total === 1 ? "" : "s"}` : null}
        actions={
          manage ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" icon={<ArrowUpTrayIcon className="h-4 w-4" />} onClick={() => setImporting(true)}>
                Import from Excel
              </Button>
              <Button icon={<PlusCircleIcon className="h-4 w-4" />} onClick={() => setEditing({})}>
                New programme
              </Button>
            </div>
          ) : (
            <span className="flex items-center gap-2 text-sm text-slate-500">
              <EyeIcon className="h-4 w-4" /> Read-only
            </span>
          )
        }
      />

      <Table cardView
        columns={columns}
        data={data?.items ?? []}
        isLoading={isPending}
        searchPlaceholder="Search by name or institution"
        searchValue={search}
        onSearchChange={setSearch}
        exportName="abroad-loan-programmes"
        emptyTitle={isError ? "Could not load the programmes. Please try again later." : "No programmes found"}
        pagination={{ page, totalPages: data ? Math.ceil(data.total / PAGE_SIZE) : 0, totalElements: data?.total, pageSize: PAGE_SIZE, onPageChange: setPage }}
      />

      <ProgrammeDialog
        open={editing !== null}
        programme={editing?.id ? editing : null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          queryClient.invalidateQueries({ queryKey: ABROAD_KEY });
        }}
      />
      {importing && (
        <ProgrammeImportDialog
          open
          place="optional"
          saveRow={saveImported}
          onImported={() => queryClient.invalidateQueries({ queryKey: ABROAD_KEY })}
          onClose={() => setImporting(false)}
        />
      )}
    </Container>
  );
}
